/** @jsx isolateComponent */
import { css } from '@emotion/react';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { ServiceIcon, ServiceIconSize } from '../ServiceIcon';
import { ServiceIconConfig } from '../ServiceIcon/constants';
import { ServiceIconNumeric } from './ServiceIconNumeric';

export type Service = {
  name: string;
  src: string;
};

type ServiceIconsProps<T extends Service> = {
  /**
   * Label to render for screenreaders.
   */
  ariaLabel?: string;
  /**
   * Maxmium number of service icons to render. If more are provided, the final "icon" will be a number representing the number of unrendered services.
   */
  maxServices: number;
  /**
   * An array of service icons to render.
   *
   * - `name` is the name of the service whose icon is being displayed (used as alt text for the icon), e.g., "Google Sheets".
   * - `src` is a URL for the service icon image to be rendered. If this is an Imgix URL, a large-format image can be used and the image will automatically be resized.
   */
  services: Array<T>;
  /**
   * Sets the width and height of each icon (since it is a square), in pixels.
   * The list of possible sizes is finite to enforce consistency across our UI.
   */
  size: ServiceIconSize;
  /**
   * Controls the presence/absence of the border. It is recommended that a borderless ServiceIcon be
   * contained in a container with non primary color background.
   */
  hasBorder?: boolean;
  /**
   * Component to render the service icons
   */
  renderServiceIcon: (
    service: T,
    props: ServiceIconsProps<T>
  ) => React.ReactNode;
  /**
   * Changes the background color to white (#fff)
   */
  hasWhiteBackground?: boolean;
};

const Styles = labelStyles('ServiceIcons', {
  root: (iconSpacing: number) => css`
    display: inline-grid;
    user-select: none;
    grid-gap: ${iconSpacing}px;
    grid-auto-flow: column;
  `,
});

/**
 * Renders icons representing loose collection of services (aka apps, aka
 * integrations).
 *
 * The aesthetics of this component are intended to convey that the services
 * are related.
 */
export const ServiceIcons = <T extends Service>(
  props: ServiceIconsProps<T>
) => {
  const {
    maxServices,
    services,
    size,
    renderServiceIcon,
    hasBorder = true,
    hasWhiteBackground = false,
  } = props;
  const { spacing } = ServiceIconConfig[size];

  const label = props.ariaLabel || `${services.length} apps mentioned`;

  const hasExtraServices = services.length > maxServices;

  // +1 to account for the extra hidden icon to make space for the +X block.
  const numServicesHidden = hasExtraServices
    ? services.length - maxServices + 1
    : 0;
  // -1 to account for the extra hidden icon to make space for the +X block.
  const numServicesShown = hasExtraServices ? maxServices - 1 : services.length;

  // Guarantee that we render the first and last service in the provided list.
  // Prefer showing the first service if that's all there's room for.
  let visibleServices: Array<T> = [];

  if (numServicesShown === 1) {
    visibleServices = [services[0]];
  } else if (numServicesShown >= 2) {
    visibleServices = [
      ...services.slice(0, numServicesShown - 1),
      services[services.length - 1],
    ];
  }

  // Using `idx` as `key` since the same `service` could potentially
  // appear multiple times and therefore would not be unique.
  const icons = visibleServices.map((service, idx) => (
    <li key={idx}>{renderServiceIcon(service, props)}</li>
  ));

  if (hasExtraServices) {
    const hiddenServicesCountIcon = (
      <li key={`remainder-${numServicesHidden}`}>
        <ServiceIconNumeric
          size={size}
          number={numServicesHidden}
          hasBorder={hasBorder}
          hasWhiteBackground={hasWhiteBackground}
        />
      </li>
    );

    if (maxServices > 2) icons.splice(-1, 0, hiddenServicesCountIcon);
    else icons.push(hiddenServicesCountIcon);
  }

  return (
    <ul aria-label={label} css={Styles.root(spacing)}>
      {icons}
    </ul>
  );
};

function defaultRenderServiceIcon<T extends Service>(
  { name, src }: Service,
  { size, hasBorder, hasWhiteBackground }: ServiceIconsProps<T>
) {
  return (
    <ServiceIcon
      serviceName={name}
      size={size}
      src={src}
      hasBorder={hasBorder}
      hasWhiteBackground={hasWhiteBackground}
    />
  );
}

ServiceIcons.defaultProps = {
  hasBorder: true,
  maxServices: 6,
  renderServiceIcon: defaultRenderServiceIcon,
  size: 40,
};
